import React from 'react';
import { motion } from 'framer-motion';
import { Check, X, Clock, DollarSign, Brain, Shield, Copy } from 'lucide-react';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
export function ValueAndAudience() {
  return <section className="py-24 bg-white" data-id="element-360">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-361">
        {/* What you're really buying */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="bg-blush-50 rounded-3xl p-8 md:p-12 mb-20 border border-blush-100" data-id="element-362">
          <div className="text-center mb-10" data-id="element-363">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-ink-900 mb-4" data-id="element-364">
              💡 ESTO ES LO QUE REALMENTE ESTÁS COMPRANDO
            </h2>
            <p className="text-xl text-ink-800" data-id="element-365">
              No estás comprando un PDF. Estás comprando:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-366">
            {[{
            icon: <Clock className="w-6 h-6" data-id="element-367" />,
            title: 'Tiempo',
            desc: 'porque el método ya tiene todo resuelto por ti'
          }, {
            icon: <DollarSign className="w-6 h-6" data-id="element-368" />,
            title: 'Ingresos',
            desc: 'porque el sistema está probado para generar ganancia real'
          }, {
            icon: <Brain className="w-6 h-6" data-id="element-369" />,
            title: 'Mentalidad',
            desc: 'porque aprenderás a ver oportunidades donde otros ven solo productos baratos'
          }, {
            icon: <Shield className="w-6 h-6" data-id="element-370" />,
            title: 'Confianza',
            desc: 'porque cuando sabes exactamente qué hacer, el miedo desaparece'
          }, {
            icon: <Copy className="w-6 h-6" data-id="element-371" />,
            title: 'Un negocio replicable',
            desc: 'que puedes repetir en Navidad, San Valentín, Halloween...'
          }].map((item, i) => <div key={i} className="bg-white p-6 rounded-xl shadow-sm flex flex-col items-center text-center" data-id="element-372">
                <div className="w-12 h-12 bg-gold-400/20 text-gold-500 rounded-full flex items-center justify-center mb-4" data-id="element-373">
                  {item.icon}
                </div>
                <h4 className="font-bold text-lg text-ink-900 mb-2" data-id="element-374">
                  {item.title}
                </h4>
                <p className="text-ink-800 text-sm" data-id="element-375">{item.desc}</p>
              </div>)}
          </div>
        </motion.div>

        {/* Audience Split */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-376">
          {/* For You */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-cream-50 p-8 md:p-10 rounded-3xl border-t-8 border-emerald-900 shadow-lg" data-id="element-377">
            <h3 className="text-2xl font-bold text-ink-900 mb-8 flex items-center" data-id="element-378">
              <span className="bg-emerald-900 text-white w-10 h-10 rounded-full flex items-center justify-center mr-4" data-id="element-379">
                👩‍💼
              </span>
              ESTO ES PARA TI SI...
            </h3>
            <ul className="space-y-5" data-id="element-380">
              {['Quieres generar ingresos extras este Mayo, no el año que viene', 'Tienes menos de $50 para invertir inicialmente', 'Vendes por WhatsApp, Instagram, Facebook o en persona', 'No tienes experiencia en manualidades pero sí tienes ojos y creatividad', 'Eres mamá, estudiante, empleada o cualquier mujer que quiere su propio ingreso', 'Estás cansada de ver a otras ganar dinero con ideas que tú también podrías ejecutar'].map((text, i) => <li key={i} className="flex items-start" data-id="element-381">
                  <Check className="w-6 h-6 text-emerald-800 mr-3 flex-shrink-0 mt-0.5" data-id="element-382" />
                  <span className="text-ink-800 leading-relaxed" data-id="element-383">{text}</span>
                </li>)}
            </ul>
          </motion.div>

          {/* Not For You */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-white p-8 md:p-10 rounded-3xl border-t-8 border-red-200 shadow-lg" data-id="element-384">
            <h3 className="text-2xl font-bold text-ink-900 mb-8 flex items-center" data-id="element-385">
              <span className="bg-red-100 text-red-600 w-10 h-10 rounded-full flex items-center justify-center mr-4" data-id="element-386">
                🚫
              </span>
              ESTO NO ES PARA TI SI...
            </h3>
            <ul className="space-y-5" data-id="element-387">
              {['Buscas hacerte millonaria sin hacer nada', 'No estás dispuesta a invertir 3 horas en ir a las tiendas', <span data-id="element-388">
                  Crees que los productos baratos no pueden verse lujosos{' '}
                  <em className="text-ink-900/60" data-id="element-389">(spoiler: pueden)</em>
                </span>, <span data-id="element-390">
                  Prefieres esperar a que "el momento perfecto" llegue{' '}
                  <em className="text-ink-900/60" data-id="element-391">(no llega — tú lo creas)</em>
                </span>].map((text, i) => <li key={i} className="flex items-start" data-id="element-392">
                  <X className="w-6 h-6 text-red-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-393" />
                  <span className="text-ink-800 leading-relaxed" data-id="element-394">{text}</span>
                </li>)}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>;
}